import { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Mail, Check, Copy, AlertCircle } from "lucide-react";

function splitName(full = "") {
  const parts = full.trim().split(/\s+/);
  if (parts.length <= 1) return { first: parts[0] || "", last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

export function LeadCapture({ respondentId, about = {} }) {
  const seed = splitName(about.name);
  const [form, setForm] = useState({
    first_name: seed.first,
    last_name: seed.last,
    email: "",
    organisation: about.organisation || "",
    wants_conversation: false,
  });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | failed
  const [sentTo, setSentTo] = useState("");
  const [resultsUrl, setResultsUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return; // prevent duplicate sends
    if (!form.first_name.trim()) {
      toast.error("Please enter your first name.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setStatus("sending");
    try {
      const res = await api.sendResults(respondentId, form);
      setResultsUrl(res.results_url || "");
      if (res.status === "sent") {
        setSentTo(form.email);
        setStatus("sent");
      } else {
        setStatus("failed");
        toast.error("We couldn't send your email. Please try again.");
      }
    } catch {
      setStatus("failed");
      toast.error("We couldn't send your email. Please try again.");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(resultsUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — please copy the link manually.");
    }
  };

  const retry = async () => {
    if (status === "sending") return;
    setStatus("sending");
    try {
      const res = await api.sendResults(respondentId, form);
      setResultsUrl(res.results_url || resultsUrl); // reuse the same stable token
      if (res.status === "sent") {
        setSentTo(form.email);
        setStatus("sent");
      } else {
        setStatus("failed");
        toast.error("We still couldn't send your email. Your results remain safe.");
      }
    } catch {
      setStatus("failed");
      toast.error("We still couldn't send your email. Your results remain safe.");
    }
  };

  // Email failed but the diagnostic WAS saved and a private link exists.
  // Never show a false email-success state — surface the saved link + retry.
  if (resultsUrl && status !== "sent") {
    return (
      <div
        className="rounded-sm border border-neutral-200 bg-neutral-50 p-8 text-center sm:p-12"
        data-testid="lead-capture-saved-panel"
      >
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#1408F7]">
          <Check className="h-6 w-6 text-white" />
        </div>
        <h3
          className="mt-6 text-xl font-semibold text-neutral-900"
          data-testid="lead-capture-saved-confirmation"
        >
          Your results have been saved.
        </h3>
        <p className="mx-auto mt-2 max-w-md text-neutral-600">
          We couldn&apos;t send the email, but your results are safe. You can copy your private results
          link below or try sending the email again.
        </p>
        <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={copyLink}
            className="tw-focus inline-flex items-center gap-2 rounded-sm border border-neutral-300 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-800 hover:border-neutral-900"
            data-testid="lead-capture-copy-link-button"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Link copied" : "Copy results link"}
          </button>
          <button
            type="button"
            onClick={retry}
            disabled={status === "sending"}
            className="tw-btn-primary tw-focus inline-flex items-center gap-2 rounded-sm px-5 py-3 text-xs font-semibold uppercase tracking-wide disabled:opacity-40"
            data-testid="lead-capture-retry-button"
          >
            {status === "sending" ? "Sending…" : "Try again"}
          </button>
        </div>
      </div>
    );
  }

  if (status === "sent") {
    return (
      <div className="rounded-sm border border-neutral-200 bg-neutral-50 p-8 text-center sm:p-12">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#1408F7]">
          <Check className="h-6 w-6 text-white" />
        </div>
        <h3 className="mt-6 text-xl font-semibold text-neutral-900" data-testid="lead-capture-confirmation">
          Your results are on their way.
        </h3>
        <p className="mt-2 text-neutral-600">
          We&apos;ve sent a copy to <span className="font-medium text-neutral-900">{sentTo}</span>.
        </p>
        <div className="mt-6 flex flex-col items-center gap-3">
          <button
            onClick={copyLink}
            className="tw-focus inline-flex items-center gap-2 rounded-sm border border-neutral-300 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-800 hover:border-neutral-900"
            data-testid="lead-capture-copy-link-button"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Link copied" : "Copy results link"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-sm border border-neutral-200 p-8 sm:p-12">
      <div className="flex items-center gap-3">
        <Mail className="h-5 w-5 text-[#1408F7]" strokeWidth={1.5} />
        <p className="tw-eyebrow">Keep your results</p>
      </div>
      <h3 className="mt-5 text-2xl font-semibold tracking-tight text-neutral-900">
        Want a copy of your Three Words Full diagnostic?
      </h3>
      <p className="mt-3 max-w-xl text-neutral-600">
        We&apos;ll send you your archetype, Your Three and interpretation so you can come back to your
        results later.
      </p>

      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <Label>First name</Label>
          <Input
            value={form.first_name}
            onChange={set("first_name")}
            placeholder="Jane"
            className="mt-2 h-12 rounded-sm border-neutral-300 focus-visible:ring-[#1408F7]"
            data-testid="lead-capture-first-name-input"
          />
        </div>
        <div>
          <Label>Last name</Label>
          <Input
            value={form.last_name}
            onChange={set("last_name")}
            placeholder="Doe"
            className="mt-2 h-12 rounded-sm border-neutral-300 focus-visible:ring-[#1408F7]"
            data-testid="lead-capture-last-name-input"
          />
        </div>
        <div>
          <Label>Email address</Label>
          <Input
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder="you@company.com"
            className="mt-2 h-12 rounded-sm border-neutral-300 focus-visible:ring-[#1408F7]"
            data-testid="lead-capture-email-input"
          />
        </div>
        <div>
          <Label>Organisation</Label>
          <Input
            value={form.organisation}
            onChange={set("organisation")}
            placeholder="Acme Inc."
            className="mt-2 h-12 rounded-sm border-neutral-300 focus-visible:ring-[#1408F7]"
            data-testid="lead-capture-organisation-input"
          />
        </div>
      </div>

      <label className="mt-6 flex cursor-pointer items-start gap-3">
        <Checkbox
          checked={form.wants_conversation}
          onCheckedChange={(v) => setForm((f) => ({ ...f, wants_conversation: !!v }))}
          className="mt-0.5 rounded-sm data-[state=checked]:border-[#1408F7] data-[state=checked]:bg-[#1408F7]"
          data-testid="lead-capture-conversation-checkbox"
        />
        <span className="text-sm text-neutral-600">
          I&apos;d like to talk through my results with Three Words Full.
        </span>
      </label>

      {status === "failed" && (
        <div className="mt-6 flex items-center gap-2 rounded-sm border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          We couldn&apos;t send your email. Your results are safe — please try again.
        </div>
      )}

      <div className="mt-8">
        <button
          type="submit"
          disabled={status === "sending"}
          className="tw-btn-primary tw-focus rounded-sm px-8 py-4 text-sm font-semibold uppercase tracking-wide disabled:opacity-40"
          data-testid="lead-capture-submit-button"
        >
          {status === "sending" ? "Sending…" : status === "failed" ? "Try again" : "Send my results"}
        </button>
      </div>
    </form>
  );
}
